
import { Autocomplete } from '@mui/material'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import { SxProps, useTheme } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import React from 'react'
import { OrganizationTableItem } from 'types/organizations_table'
import { renderColorByString } from '../DataGrid/renderColorByString'

interface OptionType {
  id: string
  name?: string // For organizations
  dba?: string // DBA field if needed
  firstName?: string // For users
  lastName?: string // For users
  image_512?: string // For avatars
  org_uuid: string
  onboardingStatus: string
  status: string
  [key: string]: any
}

type AutocompleteOption = OrganizationTableItem | OptionType

interface FormAutocompleteProps {
  data: AutocompleteOption[]
  isLoading: boolean
  label?: string
  error?: boolean
  onChange: (selected: AutocompleteOption | null) => void
  onClear?: () => void
  value: AutocompleteOption | null
  disabled: boolean
  sx?: SxProps
  shrinkLabel?: boolean
  placeholder?: string
}

const FormAutocomplete: React.FC<FormAutocompleteProps> = ({
  data,
  isLoading,
  label,
  error,
  onChange,
  onClear,
  value,
  disabled,
  sx,
  shrinkLabel,
  placeholder,
}) => {
  const theme = useTheme()
  const handleClear = React.useCallback(() => {
    onChange?.(null)
    if (onClear) onClear()
  }, [onChange, onClear])
  return (
    <Autocomplete<AutocompleteOption>
      options={data || []}
      getOptionLabel={option => option.name || option.dba || `${option.firstName || ''} ${option.lastName || ''}`}
      // isOptionEqualToValue={(option, value) => option.id === value?.id} // Ensure correct matching
      isOptionEqualToValue={(option, value) => option.org_uuid === value?.org_uuid}
      value={value}
      onChange={(_event, newValue) => onChange(newValue)}
      loading={isLoading}
      renderOption={(props, option) => {
        const { key, ...rest } = props
        return (
          <Box
            key={option?.id || option?.SK}
            {...rest}
            component="li"
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%' }}
          >
            <div className={option?.isOnboarding ? 'onboarding-org' : ''}>
              {option.image_512 && (
                <Avatar alt={option.name || option.firstName} src={option.image_512} sx={{ height: 36, width: 36 }} />
              )}
              <Box sx={{ display: 'flex', width: '100%' }}>
                {option.name || option.dba || `${option.firstName || ''} ${option.lastName || ''}`}
              </Box>
              {option?.onboardingStatus && (
                <Box
                  sx={{
                    display: 'flex',
                    width: '30%',
                    color: renderColorByString(option?.onboardingStatus),
                    fontSize: 12,
                  }}
                >
                  {option.onboardingStatus}
                </Box>
              )}
              {option?.status && (
                <Box sx={{ display: 'flex', width: '30%', color: renderColorByString(option?.status), fontSize: 12 }}>
                  {option.status}
                </Box>
              )}
            </div>
          </Box>
        )
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={label || 'Select'}
          variant="outlined"
          InputLabelProps={{
            shrink: shrinkLabel ?? false,
            sx: {
              color: disabled ? theme.palette.text.disabled : theme.palette.text.primary,
              WebkitTextFillColor: disabled ? theme.palette.text.disabled : theme.palette.text.primary,
            },
          }}
          placeholder={placeholder}
          error={error}
          sx={
            sx
              ? { ...sx }
              : {
                  '& .MuiInputBase-root': {
                    padding: '8px 12px',
                    bgcolor: disabled ? theme.palette.background.disabled : theme.palette.background.dialogtext,
                  },
                  '& .MuiOutlinedInput-root': {
                    minHeight: '56px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: !value ? `2px solid ${theme.palette.text.primary}` : '',
                  },
                  '& input.Mui-disabled': {
                    color: theme.palette.text.highlight, // or theme.palette.text.secondary/highlight
                    WebkitTextFillColor: theme.palette.text.highlight, // ✅ for Safari support
                  },
                }
          }
        />
      )}
      fullWidth
      componentsProps={{
        clearIndicator: {
          onClick: (e: React.MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()
            handleClear()
          },
        },
      }}
      disabled={disabled}
    />
  )
}

export default FormAutocomplete
